/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * CoordinateManager.tsx
 * Gestione coordinate GPS con due modalità:
 * - Ricognizione: lista completa con dettagli e cattura rapida
 * - Briefing: vista compatta per assegnazione squadre
 */
// src/components/Coordinate/CoordinateManager.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { CoordinateCard } from './CoordinateCard';
import { GPSModal } from './GPSModal';
import './CoordinateManager.css';

const TIPO_META: Record<string, { emoji: string; label: string; color: string }> = {
  fronte:         { emoji: '🔥', label: 'Fronte',   color: '#ef4444' },
  vedetta:        { emoji: '👁',  label: 'Vedetta',  color: '#f59e0b' },
  risorsa_idrica: { emoji: '💧', label: 'Acqua',    color: '#3b82f6' },
  base:           { emoji: '🏠', label: 'Base',     color: '#22c55e' },
  altro:          { emoji: '📍', label: 'Altro',    color: '#94a3b8' },
};

export const CoordinateManager: React.FC = () => {
  const [showGPSModal,   setShowGPSModal]   = useState(false);
  const [filterAssigned, setFilterAssigned] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [searchText,     setSearchText]     = useState('');
  const [modoBriefing,   setModoBriefing]   = useState(false);

  const coordinate     = useAppStore(state => state.coordinate);
  const personale      = useAppStore(state => state.personale);
  const updateCoordinate = useAppStore(state => state.updateCoordinate);
  const deleteCoordinate = useAppStore(state => state.deleteCoordinate);

  // Capisquadra per briefing inline
  const capiSquadra = personale.filter(p => p.qualifica === 'capo_squadra');
  const capiGiaAssegnati = new Set<string>();
  coordinate.forEach(c => { if (c.capoSquadra) capiGiaAssegnati.add(c.capoSquadra); });

  // Statistiche
  const stats = {
    totali:       coordinate.length,
    assegnate:    coordinate.filter((c, i) => i > 0 && c.capoSquadra).length,
    nonAssegnate: coordinate.filter((c, i) => i > 0 && !c.capoSquadra).length,
  };

  // Filtra
  const coordinateFiltrate = coordinate.filter((c, index) => {
    let matchAssigned = true;
    if (filterAssigned === 'assigned'   && index > 0) matchAssigned = !!c.capoSquadra;
    if (filterAssigned === 'unassigned' && index > 0) matchAssigned = !c.capoSquadra;

    let matchSearch = true;
    if (searchText) {
      const s = searchText.toLowerCase();
      matchSearch =
        c.note?.toLowerCase().includes(s)        ||
        c.titolo?.toLowerCase().includes(s)      ||
        c.capoSquadra?.toLowerCase().includes(s) ||
        String(c.lat).includes(s)                ||
        String(c.lon).includes(s);
    }
    return matchAssigned && matchSearch;
  });

  const handleToggle     = (id: number, checked: boolean) => updateCoordinate(id as any, { active: checked } as any);
  const handleUpdateNote = (id: number, note: string)     => updateCoordinate(id as any, { note } as any);
  const handleDelete     = (id: number) => {
    if (confirm('Eliminare questa coordinata?')) deleteCoordinate(id as any);
  };

  const handleAssegnaBriefing = (coordId: any, nomeCompleto: string) => {
    const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    updateCoordinate(coordId as any, {
      capoSquadra: nomeCompleto,
      timestampAssegnazione: nomeCompleto ? ora : '',
    } as any);
  };

  return (
    <div className="coordinate-manager">

      {/* HEADER */}
      <div className="coordinate-header" style={{ gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>📍 Coordinate GPS</h2>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {/* Toggle Briefing */}
          <button
            onClick={() => setModoBriefing(v => !v)}
            style={{
              padding: '10px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13,
              border: modoBriefing ? 'none' : '2px solid rgba(255,255,255,0.2)',
              background: modoBriefing
                ? 'linear-gradient(135deg, #1e3a5f, #2563eb)'
                : 'rgba(255,255,255,0.08)',
              color: modoBriefing ? '#fff' : '#94a3b8',
              cursor: 'pointer',
            }}
          >
            {modoBriefing ? '✏️ Ricognizione' : '📋 Briefing'}
          </button>

          {/* Acquisisci GPS */}
          <button className="btn-acquire-gps" onClick={() => setShowGPSModal(true)}>
            <span className="icon">📡</span>
            <span>Acquisisci GPS</span>
          </button>
        </div>
      </div>

      {/* STATISTICHE — cliccabili come filtri */}
      <div className="stats-container">
        <div
          className={`stat-card stat-total${filterAssigned === 'all' ? ' active' : ''}`}
          onClick={() => setFilterAssigned('all')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">📍</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totali}</div>
            <div className="stat-label">Totali</div>
          </div>
        </div>
        <div
          className={`stat-card stat-assigned${filterAssigned === 'assigned' ? ' active' : ''}`}
          onClick={() => setFilterAssigned('assigned')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.assegnate}</div>
            <div className="stat-label">Assegnate</div>
          </div>
        </div>
        <div
          className={`stat-card stat-unassigned${filterAssigned === 'unassigned' ? ' active' : ''}`}
          onClick={() => setFilterAssigned('unassigned')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{stats.nonAssegnate}</div>
            <div className="stat-label">Da assegnare</div>
          </div>
        </div>
      </div>

      {/* ── MODO BRIEFING ─────────────────────────────────────────────── */}
      {modoBriefing && (
        <div style={S.briefingContainer}>
          <div style={S.briefingHeader}>
            📋 BRIEFING — Assegnazione posizioni alle squadre
          </div>

          {coordinate.length <= 1 ? (
            <div style={S.briefingVuoto}>
              Nessun punto da assegnare. Acquisici coordinate durante la ricognizione.
            </div>
          ) : (
            coordinate.map((c, idx) => {
              if (idx === 0) return null; // Salta Posizione DOS
              const tipo = (c as any).tipo ?? 'altro';
              const meta = TIPO_META[tipo] ?? TIPO_META.altro;
              const nome = c.titolo || `P${idx}`;
              const assegnato = c.capoSquadra?.trim();

              // Capisquadra disponibili per questa riga
              const disponibili = capiSquadra.filter(cs => {
                const n = `${cs.nome} ${cs.cognome}`.trim();
                return n === assegnato || !capiGiaAssegnati.has(n);
              });

              return (
                <div key={c.id} style={S.briefingRiga}>
                  {/* Colore tipo */}
                  <div style={{ ...S.briefingTipoBar, background: meta.color }} />

                  {/* Nome + tipo */}
                  <div style={S.briefingNome}>
                    <span style={{ fontWeight: 800, color: '#fff', fontSize: 16 }}>
                      {meta.emoji} {nome}
                    </span>
                    <span style={{ color: meta.color, fontSize: 12, fontWeight: 600 }}>
                      {meta.label}
                    </span>
                    {c.note && (
                      <span style={S.briefingNote}>{c.note}</span>
                    )}
                  </div>

                  {/* Selezione capo squadra inline */}
                  <select
                    value={assegnato ?? ''}
                    onChange={e => handleAssegnaBriefing((c as any).id, e.target.value)}
                    style={{
                      ...S.briefingSelect,
                      borderColor: assegnato ? '#22c55e' : 'rgba(255,255,255,0.2)',
                      color:       assegnato ? '#22c55e' : '#94a3b8',
                    }}
                  >
                    <option value="">— Non assegnata —</option>
                    {disponibili.map(cs => {
                      const n = `${cs.nome} ${cs.cognome}`.trim();
                      return <option key={cs.id} value={n}>{n}</option>;
                    })}
                    {/* Se assegnato a qualcuno non più disponibile, mostralo comunque */}
                    {assegnato && !disponibili.some(cs => `${cs.nome} ${cs.cognome}`.trim() === assegnato) && (
                      <option value={assegnato}>{assegnato}</option>
                    )}
                  </select>

                  {/* Stato */}
                  {assegnato && (
                    <span style={S.briefingCheck}>✅</span>
                  )}
                </div>
              );
            })
          )}

          {/* Riepilogo assegnazioni */}
          {stats.assegnate > 0 && (
            <div style={S.briefingRiepilogo}>
              ✅ {stats.assegnate} / {stats.totali - 1} punti assegnati
              {stats.nonAssegnate === 0 && <span style={{ color: '#22c55e', marginLeft: 8 }}>— Briefing completo</span>}
            </div>
          )}
        </div>
      )}

      {/* ── MODO RICOGNIZIONE ─────────────────────────────────────────── */}
      {!modoBriefing && (
        <>
          {/* BARRA DI RICERCA */}
          <div className="filters-container">
            <div className="search-bar">
              <input
                type="text"
                placeholder="🔍 Cerca per note, caposquadra, coordinate…"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="search-input"
              />
              {searchText && (
                <button className="clear-search" onClick={() => setSearchText('')}>✕</button>
              )}
            </div>
          </div>

          {/* LISTA */}
          <div className="coordinate-list">
            {coordinateFiltrate.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📍</div>
                <p className="empty-text">
                  {coordinate.length === 0
                    ? 'Nessuna coordinata rilevata'
                    : 'Nessuna coordinata corrisponde ai filtri'}
                </p>
                {coordinate.length === 0 && (
                  <button className="btn-empty-action" onClick={() => setShowGPSModal(true)}>
                    📡 Acquisisci Prima Coordinata
                  </button>
                )}
              </div>
            ) : (
              coordinateFiltrate.map(coord => {
                const realIndex    = coordinate.findIndex(c => c.id === coord.id);
                const isPosizioneDOS = realIndex === 0;
                return (
                  <CoordinateCard
                    key={coord.id}
                    coordinate={coord as any}
                    index={realIndex}
                    isPosizioneDOS={isPosizioneDOS}
                    onToggle={checked => handleToggle((coord as any).id, checked)}
                    onUpdateNote={note => handleUpdateNote((coord as any).id, note)}
                    onDelete={() => handleDelete((coord as any).id)}
                  />
                );
              })
            )}
          </div>

          {searchText && coordinateFiltrate.length > 0 && (
            <div className="filter-info">
              Visualizzate {coordinateFiltrate.length} di {coordinate.length} coordinate
            </div>
          )}
        </>
      )}

      {showGPSModal && (
        <GPSModal isOpen={showGPSModal} onClose={() => setShowGPSModal(false)} />
      )}
    </div>
  );
};

// ─── Stili briefing ───────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  briefingContainer: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(37,99,235,0.3)',
    borderRadius: 12, overflow: 'hidden',
    marginBottom: 20,
  },
  briefingHeader: {
    background: 'linear-gradient(135deg, #0d1520, #1e3a5f)',
    color: '#60a5fa', fontWeight: 700, fontSize: 14,
    padding: '14px 18px', letterSpacing: 0.3,
  },
  briefingVuoto: {
    color: '#64748b', fontSize: 13, textAlign: 'center', padding: '20px',
  },
  briefingRiga: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  briefingTipoBar: {
    width: 4, height: 36, borderRadius: 2, flexShrink: 0,
  },
  briefingNome: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: 2,
    minWidth: 0,
  },
  briefingNote: {
    color: '#64748b', fontSize: 11,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  briefingSelect: {
    padding: '8px 12px', borderRadius: 7,
    border: '2px solid',
    background: '#0f1a2e',
    fontSize: 13, fontWeight: 600,
    cursor: 'pointer', minWidth: 180,
    outline: 'none',
  },
  briefingCheck: {
    fontSize: 18, flexShrink: 0,
  },
  briefingRiepilogo: {
    padding: '12px 18px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    color: '#94a3b8', fontSize: 13, fontWeight: 600,
  },
};
